import type { Me } from "@/api/types";

/**
 * The store scope lives in the URL: `/{storeId}/opportunities`, or `/ALL/opportunities` for every
 * store the user can access. Each tab keeps its own scope (sent as `X-Store-Id` on every
 * request), so several tabs can show different stores without interfering.
 */
export const ALL_STORES = "ALL";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const looksLikeStoreId = (v: string) => UUID.test(v);

const storageKey = (orgId: string) => `rogue.lastStore.${orgId}`;

/** Where a new tab starts: the only store, else the last scope used in this browser, else ALL. */
export function defaultScope(me: Me): string {
  if (me.accessibleStores.length === 1) return me.accessibleStores[0]?.storeId ?? ALL_STORES;
  try {
    const last = localStorage.getItem(storageKey(me.organization.id));
    if (last && me.accessibleStores.some((s) => s.storeId === last)) return last;
  } catch {
    /* storage unavailable */
  }
  return ALL_STORES;
}

/** Remembers the scope as the default for new tabs (open tabs keep their own URL). */
export function rememberScope(me: Me, scope: string) {
  try {
    localStorage.setItem(storageKey(me.organization.id), scope);
  } catch {
    /* storage unavailable */
  }
}

/**
 * The canonical URL value for a requested scope, or null when the user can't use it. A user with
 * one store always works in that store (ALL means the same thing for them).
 */
export function canonicalScope(me: Me, requested: string): string | null {
  if (requested.toUpperCase() === ALL_STORES) {
    return me.accessibleStores.length === 1 ? (me.accessibleStores[0]?.storeId ?? ALL_STORES) : ALL_STORES;
  }
  return me.accessibleStores.some((s) => s.storeId === requested) ? requested : null;
}

/** The same page in another scope: `/{scope}/{section}`; detail pages go back to their list. */
export function pathInScope(pathname: string, searchStr: string, scope: string): string {
  const [, , section, ...rest] = pathname.split("/");
  const base = `/${scope}/${section || "overview"}`;
  return rest.filter(Boolean).length ? base : `${base}${searchStr}`;
}
